import { LucideIcon } from "lucide-react";
import {
  DollarSign,
  BarChart3,
  Receipt,
  Sparkles,
  Send,
  FileDown,
  Zap,
  AtSign,
  PieChart,
  Bot,
  Gift,
  UserCircle,
  Users,
  Calendar,
  Dumbbell,
  Video,
  ListChecks,
  LayoutTemplate,
  ClipboardList,
  UserPlus,
  TrendingUp,
  MessageCircle,
} from "lucide-react";

export interface PageTipConfig {
  path: string;
  title: string;
  description: string;
  icon: LucideIcon;
  valueProp: string;
  tips: string[];
  videoUrl?: string;
  spotterSuggestions: string[];
  relatedPages?: { path: string; label: string }[];
}

export const PAGE_TIPS: PageTipConfig[] = [
  // =====================
  // BUSINESS SECTION
  // =====================
  {
    path: "/earnings",
    title: "Financials",
    description: "Track your income and expenses",
    icon: DollarSign,
    valueProp: "Track every dollar earned and understand your business health at a glance. See trends, compare periods, and make data-driven decisions.",
    tips: [
      "Log earnings immediately after sessions to maintain accurate records",
      "Use the pay period feature to align with your gym's payment schedule",
      "Export data monthly for tax preparation and bookkeeping",
      "Track by client to identify your highest-value relationships",
      "Compare periods to spot growth trends or seasonal patterns",
    ],
    spotterSuggestions: [
      "How much did I earn this month?",
      "Show my top earning clients",
      "Compare this month to last month",
    ],
    relatedPages: [
      { path: "/insights", label: "View Insights" },
      { path: "/invoices", label: "Manage Invoices" },
    ],
  },
  {
    path: "/insights",
    title: "Pulse Check",
    description: "Analyze your schedule patterns",
    icon: BarChart3,
    valueProp: "Discover patterns in your schedule and find opportunities to grow revenue without burning out. Data-driven insights for smarter business decisions.",
    tips: [
      "Aim for 60-80% utilization - leaving room for life and growth",
      "Identify your most productive time slots and protect them",
      "Look for gaps you could fill with group sessions or new clients",
      "Use the forecast to plan vacations without losing income",
      "Review weekly to catch scheduling inefficiencies early",
    ],
    spotterSuggestions: [
      "What's my utilization rate?",
      "When are my busiest times?",
      "How can I optimize my schedule?",
    ],
    relatedPages: [
      { path: "/earnings", label: "View Financials" },
      { path: "/calendar", label: "Manage Calendar" },
    ],
  },
  {
    path: "/invoices",
    title: "Invoices",
    description: "Create and manage client invoices",
    icon: Receipt,
    valueProp: "Professional invoicing that gets you paid faster. Create, send, and track invoices with automatic payment reminders.",
    tips: [
      "Send invoices immediately after package purchase for faster payment",
      "Use recurring invoices for monthly memberships",
      "Enable automatic payment reminders to reduce follow-up time",
      "Add detailed line items to avoid client questions",
      "Track overdue invoices weekly to maintain cash flow",
    ],
    spotterSuggestions: [
      "Show my outstanding invoices",
      "Create an invoice for a client",
      "What's my accounts receivable?",
    ],
    relatedPages: [
      { path: "/earnings", label: "View Financials" },
      { path: "/clients", label: "View Clients" },
    ],
  },

  // =====================
  // MARKETING SECTION
  // =====================
  {
    path: "/marketing/content",
    title: "Content Studio",
    description: "AI-powered content generation",
    icon: Sparkles,
    valueProp: "Generate weeks of engaging social content in minutes. AI drafts posts tailored to your style and fitness niche - you just edit and post.",
    tips: [
      "Batch create content monthly, then schedule weekly for consistency",
      "Mix content types: educational (40%), motivational (30%), promotional (30%)",
      "Add your personal touches before posting - AI is a starting point",
      "Save high-performers as templates for future inspiration",
      "Repurpose one piece into multiple formats (post, story, reel caption)",
    ],
    spotterSuggestions: [
      "Create a week of content for me",
      "What type of content performs best?",
      "Help me plan my content calendar",
    ],
    relatedPages: [
      { path: "/marketing/social", label: "Schedule Posts" },
      { path: "/marketing/emails", label: "Email Campaigns" },
    ],
  },
  {
    path: "/marketing/emails",
    title: "Email Campaigns",
    description: "Send targeted email campaigns",
    icon: Send,
    valueProp: "Stay top-of-mind with leads and clients through targeted email sequences. Nurture relationships automatically while you focus on training.",
    tips: [
      "Welcome sequences convert 3x better than one-off emails",
      "Segment your list: leads, active clients, past clients",
      "Best send times: Tuesday-Thursday, 10am or 8pm local time",
      "Keep subject lines under 50 characters for mobile",
      "Include one clear call-to-action per email",
    ],
    spotterSuggestions: [
      "Help me write a welcome email",
      "What's my email open rate?",
      "Create a re-engagement campaign",
    ],
    relatedPages: [
      { path: "/marketing/automations", label: "Set Up Automations" },
      { path: "/leads", label: "View Leads" },
    ],
  },
  {
    path: "/marketing/lead-magnets",
    title: "Lead Magnets",
    description: "Create downloadable resources",
    icon: FileDown,
    valueProp: "Capture leads with valuable free resources. Create workout plans, nutrition guides, and fitness challenges that showcase your expertise.",
    tips: [
      "Solve one specific problem your ideal client has",
      "Keep it actionable - something they can use immediately",
      "Include your branding and contact info on every page",
      "Gate with email capture to build your list",
      "Promote on social media and your website consistently",
    ],
    spotterSuggestions: [
      "Create a 7-day workout plan",
      "Help me make a nutrition guide",
      "What lead magnets work best for trainers?",
    ],
    relatedPages: [
      { path: "/marketing/content", label: "Content Studio" },
      { path: "/leads", label: "View Leads" },
    ],
  },
  {
    path: "/marketing/automations",
    title: "Automations",
    description: "Set up automated workflows",
    icon: Zap,
    valueProp: "Turn manual follow-ups into automatic nurture sequences that work while you train. Never let a lead go cold again.",
    tips: [
      "Start with a lead nurture workflow - it has the highest ROI",
      "Set up birthday and milestone automations for client retention",
      "Use if/then branching to personalize based on behavior",
      "Test workflows with yourself before activating for real leads",
      "Review and optimize automations monthly based on results",
    ],
    spotterSuggestions: [
      "Help me create a lead nurture sequence",
      "What automations should I set up first?",
      "Show my active automations",
    ],
    relatedPages: [
      { path: "/marketing/emails", label: "Email Campaigns" },
      { path: "/leads", label: "View Leads" },
    ],
  },
  {
    path: "/marketing/social",
    title: "Social Media",
    description: "Schedule and publish posts",
    icon: AtSign,
    valueProp: "Manage all your social channels in one place. Schedule posts, track engagement, and grow your following without living on your phone.",
    tips: [
      "Post consistently - 3-5 times per week beats sporadic posting",
      "Best times: 6-9am and 6-9pm when your audience is active",
      "Engage with comments within 1 hour for algorithm boost",
      "Use hashtags strategically - 5-10 relevant ones per post",
      "Repurpose top content across platforms with slight tweaks",
    ],
    spotterSuggestions: [
      "When should I post for best engagement?",
      "Schedule my content for this week",
      "What hashtags should I use?",
    ],
    relatedPages: [
      { path: "/marketing/content", label: "Create Content" },
      { path: "/marketing/roi", label: "View Analytics" },
    ],
  },
  {
    path: "/marketing/roi",
    title: "ROI Dashboard",
    description: "Track marketing performance",
    icon: PieChart,
    valueProp: "See exactly which marketing efforts bring in paying clients. Stop guessing and double down on what actually works for your business.",
    tips: [
      "Focus on cost-per-acquisition, not just total leads",
      "Compare channel performance monthly to spot trends",
      "Factor in your time cost for organic vs paid efforts",
      "Set up UTM tracking on all external links for accurate attribution",
      "Review ROI before increasing marketing spend anywhere",
    ],
    spotterSuggestions: [
      "What's my best lead source?",
      "Show my marketing ROI this month",
      "Where should I focus my marketing?",
    ],
    relatedPages: [
      { path: "/leads", label: "View Leads" },
      { path: "/earnings", label: "View Revenue" },
    ],
  },
  {
    path: "/marketing/agent",
    title: "AI Marketing Agent",
    description: "Your AI marketing assistant",
    icon: Bot,
    valueProp: "Your always-on marketing assistant that can plan campaigns, create content, and optimize performance. Like having a marketing team on call 24/7.",
    tips: [
      "Start with a content planning session to see what the agent can do",
      "Review all proposed actions before approving - you're always in control",
      "Use for brainstorming - the agent learns your preferences over time",
      "Check pending actions daily for best results",
      "Be specific with requests for better outputs",
    ],
    spotterSuggestions: [
      "What can you help me with?",
      "Create a marketing plan for this month",
      "Review my recent marketing performance",
    ],
    relatedPages: [
      { path: "/marketing/content", label: "Content Studio" },
      { path: "/marketing/roi", label: "View ROI" },
    ],
  },
  {
    path: "/referrals",
    title: "Referrals",
    description: "Manage your referral program",
    icon: Gift,
    valueProp: "Turn happy clients into your best salespeople. A structured referral program can become your #1 source of new clients.",
    tips: [
      "Ask for referrals right after a client hits a milestone",
      "Make it easy - provide shareable links and social posts",
      "Reward both the referrer and the new client for best results",
      "Follow up on referral leads within 24 hours",
      "Thank referrers publicly (with permission) to encourage others",
    ],
    spotterSuggestions: [
      "How many referrals did I get this month?",
      "Help me ask a client for a referral",
      "What's my best referral reward structure?",
    ],
    relatedPages: [
      { path: "/leads", label: "View Leads" },
      { path: "/clients", label: "View Clients" },
    ],
  },
  {
    path: "/profile",
    title: "Public Profile",
    description: "Your public trainer page",
    icon: UserCircle,
    valueProp: "Your professional online presence that works while you sleep. Showcase your expertise, display testimonials, and let clients book directly.",
    tips: [
      "Use a professional photo - it increases trust significantly",
      "Highlight your specialty and ideal client clearly",
      "Include 3-5 strong testimonials with before/after if possible",
      "Make your services and pricing clear to pre-qualify leads",
      "Add a clear call-to-action (book a consult, download guide, etc.)",
    ],
    spotterSuggestions: [
      "How can I improve my profile?",
      "Help me write my bio",
      "What should I include in my profile?",
    ],
    relatedPages: [
      { path: "/marketing/content", label: "Create Content" },
      { path: "/leads", label: "View Leads" },
    ],
  },

  // =====================
  // CLIENTS SECTION
  // =====================
  {
    path: "/clients",
    title: "All Clients",
    description: "Manage your client roster",
    icon: Users,
    valueProp: "Your complete client database. Track sessions, packages, progress, and communication all in one place.",
    tips: [
      "Keep client notes updated after each session for continuity",
      "Set up package renewal reminders to avoid gaps in training",
      "Use tags to segment clients for targeted communication",
      "Review inactive clients monthly for re-engagement opportunities",
      "Document client preferences and injuries for safe, personalized training",
    ],
    spotterSuggestions: [
      "Show my active clients",
      "Who needs a package renewal?",
      "List clients I haven't seen recently",
    ],
    relatedPages: [
      { path: "/calendar", label: "View Calendar" },
      { path: "/leads", label: "View Leads" },
    ],
  },
  {
    path: "/leads",
    title: "Leads",
    description: "Track potential clients",
    icon: UserPlus,
    valueProp: "Never lose a potential client again. Track every lead from first contact to conversion with automated follow-up reminders.",
    tips: [
      "Follow up within 5 minutes for 10x better conversion rates",
      "Use lead scoring to prioritize your hottest prospects",
      "Set up automated nurture sequences for longer sales cycles",
      "Track lead sources to know where to focus marketing efforts",
      "Convert leads to clients as soon as they purchase to keep data clean",
    ],
    spotterSuggestions: [
      "Show my hot leads",
      "Who should I follow up with today?",
      "What's my lead conversion rate?",
    ],
    relatedPages: [
      { path: "/clients", label: "View Clients" },
      { path: "/marketing/roi", label: "View ROI" },
    ],
  },
  {
    path: "/clients/forecast",
    title: "Forecast",
    description: "Revenue and session projections",
    icon: TrendingUp,
    valueProp: "See your business future before it happens. Predict revenue, identify at-risk clients, and plan for growth with confidence.",
    tips: [
      "Review weekly to catch potential revenue dips early",
      "Use package expiration alerts to proactively reach out",
      "Factor in seasonal patterns when planning promotions",
      "Set revenue goals and track progress against forecast",
      "Identify clients who may churn and intervene early",
    ],
    spotterSuggestions: [
      "What's my projected revenue?",
      "Which clients might churn?",
      "How many sessions do I have booked?",
    ],
    relatedPages: [
      { path: "/earnings", label: "View Financials" },
      { path: "/clients", label: "View Clients" },
    ],
  },
  {
    path: "/messages",
    title: "Messages",
    description: "Client communication",
    icon: MessageCircle,
    valueProp: "All your client conversations in one place. No more searching through texts, emails, and DMs to find that one message.",
    tips: [
      "Respond to messages within a few hours during business hours",
      "Use templates for common responses to save time",
      "Keep conversations professional and documented",
      "Set boundaries with response time expectations",
      "Archive old conversations to keep your inbox clean",
    ],
    spotterSuggestions: [
      "Show unread messages",
      "Help me write a message to a client",
      "Who haven't I responded to?",
    ],
    relatedPages: [
      { path: "/clients", label: "View Clients" },
      { path: "/calendar", label: "View Calendar" },
    ],
  },

  // =====================
  // TRAINING SECTION
  // =====================
  {
    path: "/workouts",
    title: "Workouts",
    description: "Create and manage workouts",
    icon: Dumbbell,
    valueProp: "Build professional workouts in minutes. Use AI assistance or create from scratch - then assign to any client with one click.",
    tips: [
      "Build a library of template workouts for common goals",
      "Include clear exercise descriptions and video links",
      "Use progressive overload principles in your programming",
      "Save time by duplicating and modifying existing workouts",
      "Tag workouts by goal (strength, hypertrophy, conditioning) for easy filtering",
    ],
    spotterSuggestions: [
      "Create a workout for me",
      "Show my workout library",
      "Generate a program for a client",
    ],
    relatedPages: [
      { path: "/templates", label: "View Templates" },
      { path: "/video-library", label: "Exercise Videos" },
    ],
  },
  {
    path: "/video-library",
    title: "Video Library",
    description: "Exercise demonstration videos",
    icon: Video,
    valueProp: "Professional exercise demos at your fingertips. Link videos to workouts so clients always know proper form.",
    tips: [
      "Add videos to workouts for clients training independently",
      "Create custom videos for exercises unique to your programming",
      "Organize by muscle group and movement pattern",
      "Use videos for form correction remotely",
      "Build a library over time - it compounds in value",
    ],
    spotterSuggestions: [
      "Find a video for an exercise",
      "How do I add videos to workouts?",
      "Show exercises for a muscle group",
    ],
    relatedPages: [
      { path: "/workouts", label: "View Workouts" },
      { path: "/templates", label: "View Templates" },
    ],
  },
  {
    path: "/habits",
    title: "Habits",
    description: "Track client habits",
    icon: ListChecks,
    valueProp: "Help clients build lasting habits beyond the gym. Track consistency and celebrate streaks to keep motivation high.",
    tips: [
      "Start with 1-2 simple habits - don't overwhelm clients",
      "Make habits specific and measurable (not 'eat healthy' but '3 servings of vegetables')",
      "Celebrate streaks to reinforce positive behavior",
      "Review habit compliance during check-ins",
      "Adjust habits based on client lifestyle and challenges",
    ],
    spotterSuggestions: [
      "What habits are my clients tracking?",
      "Who has the best habit streaks?",
      "Help me create a habit for a client",
    ],
    relatedPages: [
      { path: "/check-ins", label: "View Check-ins" },
      { path: "/clients", label: "View Clients" },
    ],
  },
  {
    path: "/templates",
    title: "Templates",
    description: "Workout and program templates",
    icon: LayoutTemplate,
    valueProp: "Work smarter, not harder. Build once, use forever. Templates let you deliver consistent quality at scale.",
    tips: [
      "Create templates for common client goals (weight loss, strength, etc.)",
      "Include progression schemes in your templates",
      "Document template purpose and ideal use case",
      "Review and update templates quarterly",
      "Share templates with your team for consistency",
    ],
    spotterSuggestions: [
      "Show my templates",
      "Create a template for me",
      "What templates should I have?",
    ],
    relatedPages: [
      { path: "/workouts", label: "View Workouts" },
      { path: "/clients", label: "View Clients" },
    ],
  },
  {
    path: "/check-ins",
    title: "Check-ins",
    description: "Client progress check-ins",
    icon: ClipboardList,
    valueProp: "Structured check-ins that capture what matters. Track progress, identify obstacles, and keep clients accountable between sessions.",
    tips: [
      "Schedule regular check-ins (weekly or bi-weekly)",
      "Use consistent questions to track progress over time",
      "Review check-ins before each training session",
      "Celebrate wins and address concerns promptly",
      "Use check-in data to adjust programming",
    ],
    spotterSuggestions: [
      "Show recent check-ins",
      "Who hasn't checked in this week?",
      "What should I include in check-ins?",
    ],
    relatedPages: [
      { path: "/habits", label: "View Habits" },
      { path: "/clients", label: "View Clients" },
    ],
  },

  // =====================
  // OVERVIEW SECTION
  // =====================
  {
    path: "/dashboard",
    title: "Dashboard",
    description: "Your business overview",
    icon: BarChart3,
    valueProp: "Everything you need to know at a glance. See today's schedule, key metrics, and action items all in one place.",
    tips: [
      "Check your dashboard first thing each day for a quick overview",
      "Use the calendar sync to keep everything up to date",
      "Review key metrics weekly to track business health",
      "Address alerts and action items promptly",
      "Customize your view to show what matters most to you",
    ],
    spotterSuggestions: [
      "What do I have today?",
      "How's my business doing?",
      "What should I focus on this week?",
    ],
    relatedPages: [
      { path: "/calendar", label: "View Calendar" },
      { path: "/clients", label: "View Clients" },
    ],
  },
  {
    path: "/calendar",
    title: "Calendar",
    description: "Manage your schedule",
    icon: Calendar,
    valueProp: "Your training schedule, perfectly organized. Book sessions, manage availability, and sync with your personal calendar.",
    tips: [
      "Block off personal time first - protect your work-life balance",
      "Batch similar sessions together when possible",
      "Use recurring sessions for regular clients",
      "Enable calendar sync to avoid double-booking",
      "Review next week's schedule every Friday",
    ],
    spotterSuggestions: [
      "What's on my calendar today?",
      "Schedule a session with a client",
      "When am I free this week?",
    ],
    relatedPages: [
      { path: "/dashboard", label: "View Dashboard" },
      { path: "/clients", label: "View Clients" },
    ],
  },
];

// Helper to find tips for a given path
export function getPageTips(pathname: string): PageTipConfig | undefined {
  // Exact match first
  const exactMatch = PAGE_TIPS.find(tip => tip.path === pathname);
  if (exactMatch) return exactMatch;

  // Pattern match (for paths like /clients/:id)
  return PAGE_TIPS.find(tip => {
    const pattern = tip.path.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${pattern}$`);
    return regex.test(pathname);
  });
}

// Get the section name for a path
export function getPageSection(pathname: string): string {
  if (pathname.startsWith('/marketing')) return 'Marketing';
  if (pathname.startsWith('/clients') || pathname === '/leads' || pathname === '/messages') return 'Clients';
  if (pathname === '/earnings' || pathname === '/insights' || pathname === '/invoices') return 'Business';
  if (pathname.startsWith('/workout') || pathname.startsWith('/video') || pathname === '/habits' || pathname === '/templates' || pathname === '/check-ins') return 'Training';
  if (pathname === '/dashboard' || pathname === '/calendar') return 'Overview';
  if (pathname.startsWith('/company') || pathname === '/team') return 'Company';
  return 'General';
}

// Default suggestions when no page-specific ones are available
export const DEFAULT_SPOTTER_SUGGESTIONS = [
  "How many sessions this week?",
  "Show my active clients",
  "What should I focus on today?",
];

// =====================
// SECTION-LEVEL TIPS
// =====================

export interface SectionTipConfig {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  valueProp: string;
  tips: string[];
  keyFeatures: { title: string; description: string }[];
  spotterSuggestions: string[];
  getStartedPath?: string;
}

export const SECTION_TIPS: Record<string, SectionTipConfig> = {
  overview: {
    id: "overview",
    title: "Overview",
    description: "Your command center",
    icon: BarChart3,
    valueProp: "Get a bird's eye view of your entire training business. See what's happening today, this week, and track your progress toward your goals.",
    tips: [
      "Start each day from the Dashboard to see your schedule and priorities",
      "Use the Calendar to manage your time and protect personal boundaries",
      "Check metrics weekly to understand your business trajectory",
      "Set up calendar sync to keep everything automatically updated",
    ],
    keyFeatures: [
      { title: "Dashboard", description: "Daily overview, metrics, and action items" },
      { title: "Calendar", description: "Schedule management and availability" },
    ],
    spotterSuggestions: [
      "What's my day look like?",
      "How's my business doing this month?",
      "What should I prioritize today?",
    ],
    getStartedPath: "/dashboard",
  },
  clients: {
    id: "clients",
    title: "Clients",
    description: "Manage your client relationships",
    icon: Users,
    valueProp: "Everything you need to manage client relationships from first contact to loyal regular. Track leads, manage packages, and communicate effectively.",
    tips: [
      "Respond to new leads within 5 minutes for 10x better conversion",
      "Keep client notes updated after each session for continuity",
      "Set up package renewal reminders to avoid gaps in training",
      "Use the forecast to predict revenue and identify at-risk clients",
      "Check messages regularly to maintain strong client relationships",
    ],
    keyFeatures: [
      { title: "Client List", description: "All your clients, packages, and history" },
      { title: "Leads", description: "Track potential clients and follow-ups" },
      { title: "Forecast", description: "Revenue predictions and churn risk" },
      { title: "Messages", description: "Client communication hub" },
    ],
    spotterSuggestions: [
      "Who should I follow up with?",
      "Show clients needing renewals",
      "What's my client retention rate?",
    ],
    getStartedPath: "/clients",
  },
  marketing: {
    id: "marketing",
    title: "Marketing",
    description: "Grow your training business",
    icon: Sparkles,
    valueProp: "A complete marketing toolkit designed for personal trainers. Generate content, run email campaigns, automate follow-ups, and track what's actually working.",
    tips: [
      "Start with the AI Marketing Agent to get personalized recommendations",
      "Batch create content monthly in Content Studio, then schedule weekly",
      "Set up a lead nurture automation to work while you train",
      "Check ROI Dashboard monthly to double down on what's working",
      "Build your email list with valuable lead magnets",
    ],
    keyFeatures: [
      { title: "Content Studio", description: "AI-powered social media content" },
      { title: "Email Campaigns", description: "Targeted email marketing" },
      { title: "Automations", description: "Set-and-forget follow-up sequences" },
      { title: "AI Marketing Agent", description: "Your personal marketing assistant" },
      { title: "ROI Dashboard", description: "Track what's actually working" },
    ],
    spotterSuggestions: [
      "Help me create a marketing plan",
      "What marketing should I focus on?",
      "How do I get more clients?",
    ],
    getStartedPath: "/marketing/agent",
  },
  business: {
    id: "business",
    title: "Business",
    description: "Track your financials",
    icon: DollarSign,
    valueProp: "Understand your business health at a glance. Track income, analyze patterns, and make data-driven decisions to grow profitably.",
    tips: [
      "Log earnings immediately after sessions for accurate records",
      "Review Pulse Check weekly to understand your schedule efficiency",
      "Send invoices promptly and enable payment reminders",
      "Export financial data monthly for tax preparation",
      "Compare periods to identify seasonal trends",
    ],
    keyFeatures: [
      { title: "Financials", description: "Track all income and revenue" },
      { title: "Pulse Check", description: "Schedule analysis and utilization" },
      { title: "Invoices", description: "Professional billing and payments" },
    ],
    spotterSuggestions: [
      "How much did I earn this month?",
      "What's my schedule utilization?",
      "Show my financial trends",
    ],
    getStartedPath: "/earnings",
  },
  training: {
    id: "training",
    title: "Training",
    description: "Deliver exceptional workouts",
    icon: Dumbbell,
    valueProp: "Professional workout programming made easy. Build workouts, track client habits, and manage check-ins - all the tools to deliver exceptional training.",
    tips: [
      "Build a library of template workouts for common client goals",
      "Add video demos to workouts for clients training independently",
      "Start clients with 1-2 simple habits - don't overwhelm them",
      "Use check-ins to stay connected between sessions",
      "Review and update templates quarterly to keep programming fresh",
    ],
    keyFeatures: [
      { title: "Workouts", description: "Build and assign custom workouts" },
      { title: "Video Library", description: "Exercise demonstrations" },
      { title: "Habits", description: "Track client habit streaks" },
      { title: "Templates", description: "Reusable workout templates" },
      { title: "Check-ins", description: "Structured progress tracking" },
    ],
    spotterSuggestions: [
      "Create a workout for a client",
      "What templates should I build?",
      "How do I improve client accountability?",
    ],
    getStartedPath: "/workouts",
  },
};

// Get section tips by section ID
export function getSectionTips(sectionId: string): SectionTipConfig | undefined {
  return SECTION_TIPS[sectionId.toLowerCase()];
}
